import { useState } from 'react';
import { X, FileText, Search, Check } from 'lucide-react';
import { useAllDossierFiles } from '../../hooks/queries/useDossierFiles';
import { useLinkFile } from '../../hooks/queries/useTaskFiles';
import { useAuthStore } from '../../store/useAuthStore';
import { filterFiles, formatFileSize } from '../../services/files/fileUtils';

interface Props {
  taskId: string;
  dossierId: string;
  alreadyLinkedIds: string[];
  onClose: () => void;
}

export function LinkFileModal({ taskId, dossierId, alreadyLinkedIds, onClose }: Props) {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  const { data: files = [] } = useAllDossierFiles(dossierId);
  const link = useLinkFile(taskId);
  const [query, setQuery] = useState('');

  const linkedSet = new Set(alreadyLinkedIds);
  const results = filterFiles(files, query);

  async function handleLink(fileId: string) {
    if (!collaboratorId || linkedSet.has(fileId)) return;
    try {
      await link.mutateAsync({ fileId, linkedBy: collaboratorId });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-md max-h-[75vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold">Joindre un fichier du dossier</h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-3 border-b border-neutral-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-cime-primary/30"
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {results.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500">Aucun fichier.</div>
          ) : (
            results.map((file) => {
              const isLinked = linkedSet.has(file.id);
              return (
                <button
                  key={file.id}
                  type="button"
                  disabled={isLinked || link.isPending}
                  onClick={() => handleLink(file.id)}
                  className="flex items-center gap-3 w-full px-5 py-2.5 hover:bg-neutral-50 text-left border-b border-neutral-100 disabled:cursor-not-allowed"
                >
                  <FileText className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-neutral-900 truncate">{file.filename}</div>
                    <div className="text-xs text-neutral-500">{formatFileSize(file.size)}</div>
                  </div>
                  {isLinked && <Check className="w-4 h-4 text-emerald-600" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
